/**
 * CopyableStatus Component
 *
 * A clickable status icon that copies the status value to clipboard.
 * Follows the same pattern as CopyableId, CopyableMember, CopyableSystem.
 */

import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { copyToClipboard, useTooltipPosition, getCopyIndicatorIcon, getCheckmarkIcon } from './utils';
import {
  CopyableValueTrigger,
  PortalTooltipBubble,
  TooltipStack,
  TooltipRow,
  TooltipLabel,
  TooltipHint
} from './styles';

type StatusType = 'successful' | 'failed' | 'paused';

interface CopyableStatusProps {
  status: StatusType;
  onCopy?: (value: string) => void;
  className?: string;
}

// Wrapper to fill cell width and center the status trigger
const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

// Status-specific trigger with color variants
const StatusTrigger = styled(CopyableValueTrigger)<{ $status: StatusType; $copied?: boolean }>`
  background: ${props => {
    if (props.$copied) return 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)';
    switch (props.$status) {
      case 'successful': return 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)';
      case 'failed': return 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
      case 'paused': return 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)';
    }
  }};

  color: ${props => {
    if (props.$copied) return '#16a34a';
    switch (props.$status) {
      case 'successful': return '#16a34a';
      case 'failed': return '#dc2626';
      case 'paused': return '#d97706';
    }
  }};

  border: 1px solid ${props => {
    if (props.$copied) return '#86efac';
    switch (props.$status) {
      case 'successful': return '#86efac';
      case 'failed': return '#fca5a5';
      case 'paused': return '#fcd34d';
    }
  }};
`;

const getStatusIcon = (status: StatusType): React.ReactNode => {
  switch (status) {
    case 'successful':
      return (
        <svg className="main-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'failed':
      return (
        <svg className="main-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    case 'paused':
      return (
        <svg className="main-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <rect x="6" y="4" width="4" height="16" rx="1" />
          <rect x="14" y="4" width="4" height="16" rx="1" />
        </svg>
      );
  }
};

export const CopyableStatus: React.FC<CopyableStatusProps> = ({
  status,
  onCopy,
  className,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const mainTooltipPosition = useTooltipPosition(triggerRef, isHovered && !copied, 140, 60, 10, 15);
  const successTooltipPosition = useTooltipPosition(triggerRef, copied, 80, 40, 10, 15);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const success = await copyToClipboard(status);
    if (success) {
      setCopied(true);
      onCopy?.(status);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <>
      <StatusWrapper className={className}>
        <StatusTrigger
          ref={triggerRef}
          $status={status}
          $copied={copied}
          onClick={handleCopy}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {getStatusIcon(status)}
          <span className="copy-indicator">{getCopyIndicatorIcon()}</span>
        </StatusTrigger>
      </StatusWrapper>

      {/* Main tooltip - shows on hover */}
      {ReactDOM.createPortal(
        <PortalTooltipBubble
          $visible={isHovered && !copied}
          $position={mainTooltipPosition}
          $maxWidth={140}
        >
          <TooltipStack>
            <TooltipRow $justify="between">
              <TooltipLabel>status</TooltipLabel>
              <TooltipHint>
                {getCopyIndicatorIcon()}
                click
              </TooltipHint>
            </TooltipRow>
            <TooltipRow $justify="center" style={{ marginTop: '0.25rem' }}>
              <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>{status}</span>
            </TooltipRow>
          </TooltipStack>
        </PortalTooltipBubble>,
        document.body
      )}

      {/* Success tooltip - shows after copy */}
      {ReactDOM.createPortal(
        <PortalTooltipBubble
          $visible={copied}
          $position={successTooltipPosition}
          $variant="success"
          $maxWidth={80}
        >
          <TooltipRow $justify="center">
            <span style={{ width: 12, height: 12, marginRight: 4 }}>{getCheckmarkIcon()}</span>
            copied!
          </TooltipRow>
        </PortalTooltipBubble>,
        document.body
      )}
    </>
  );
};

export default CopyableStatus;
